import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCredits } from '@/hooks/useCredits';
import { formatNumber } from '@/lib/utils';
import { Zap, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface CreditPurchaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreditPurchaseModal({ 
  open, 
  onOpenChange,
  onSuccess 
}: CreditPurchaseModalProps) {
  const { costs, purchaseCredits } = useCredits();
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const packages = costs?.packages || [
    { credits: 1000, price: 10, label: '1,000 credits', popular: false },
    { credits: 5000, price: 40, label: '5,000 credits', popular: true },
    { credits: 10000, price: 70, label: '10,000 credits', popular: false },
    { credits: 25000, price: 150, label: '25,000 credits', popular: false },
  ];

  const handlePurchase = async () => {
    if (!selectedPackage) return;

    setLoading(true);
    try {
      const checkoutUrl = await purchaseCredits(selectedPackage);
      
      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
      
      // Call success callback (though user will be redirected)
      onSuccess?.();
    } catch (error) {
      console.error('Error purchasing credits:', error);
      toast.error(
        error instanceof Error 
          ? error.message 
          : 'Failed to initiate purchase. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedPackageData = packages.find(p => p.credits === selectedPackage);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Purchase Credits
          </DialogTitle>
          <DialogDescription>
            Select a credit package to continue using all features. Credits never expire.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <RadioGroup 
            value={selectedPackage?.toString()} 
            onValueChange={(value) => setSelectedPackage(parseInt(value))}
          >
            {packages.map((pkg) => (
              <Card 
                key={pkg.credits} 
                className={cn(
                  "relative cursor-pointer transition-all",
                  selectedPackage === pkg.credits && "border-primary ring-2 ring-primary",
                  pkg.popular && "border-blue-200 dark:border-blue-900"
                )}
                onClick={() => setSelectedPackage(pkg.credits)}
              >
                {pkg.popular && (
                  <Badge 
                    className="absolute -top-2 right-4 bg-blue-500 text-white"
                    variant="default"
                  >
                    Most Popular
                  </Badge>
                )}
                
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value={pkg.credits.toString()} />
                      <div>
                        <h3 className="font-semibold text-lg">
                          {formatNumber(pkg.credits)} Credits
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          ${(pkg.price / pkg.credits * 1000).toFixed(2)} per 1,000 credits
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">${pkg.price}</p>
                      <p className="text-xs text-muted-foreground">one-time</p>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-green-500" />
                    <span>Credits never expire</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </RadioGroup>

          {/* What you can do with credits */}
          <Card className="bg-muted/50">
            <CardHeader className="pb-3">
              <CardDescription>What can you do with credits?</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>
                    <strong>Content Generation:</strong> Create 10 pieces of content per 1,000 credits
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>
                    <strong>AI Images:</strong> Generate 10 images per 1,000 credits
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>
                    <strong>Stock Media:</strong> Search 100 stock images or 10 videos per 1,000 credits
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div>
              {selectedPackageData && (
                <p className="text-sm text-muted-foreground">
                  Total: <span className="font-semibold text-foreground">${selectedPackageData.price}</span>
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePurchase}
                disabled={!selectedPackage || loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {loading ? 'Processing...' : 'Purchase Credits'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}